import Layout from "@/components/Layout";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { ProductImageThumb } from "@/components/catalog/ProductImageThumb";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import PermissionDenied from "@/components/PermissionDenied";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { SaleProductPicker } from "@/components/sales/SaleProductPicker";
import { useGridPagination } from "@/hooks/useGridPagination";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { resolveProductThumbnailUrl } from "@/lib/catalog/productThumbnail";
import { listCustomers, syncCustomersFromApi } from "@/lib/customers/customersStore";
import { salesIndexErrorMessage, syncSalesFromApi, type SaleIndexRecord } from "@/lib/sales/salesIndexStore";
import type { SaleProductPick } from "@/lib/sales/saleProductSearchStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { motion } from "framer-motion";
import { CalendarIcon, Edit, Eye, Filter, LayoutGrid, List, Loader2, Plus, Printer, RefreshCw, Search, Undo2, User, Wallet } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "online", label: "Online" },
  { value: "pay_order", label: "Pay Order" },
] as const;

function formatSalePrice(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(Number(amount))) return "—";
  return formatCurrency(Number(amount));
}

function paymentMethodLabel(method: string | null | undefined): string {
  if (method === "cash") return "Cash";
  if (method === "online") return "Online";
  if (method === "pay_order") return "Pay Order";
  return "—";
}

function paymentMethodBadge(method: string | null | undefined): { label: string; cls: string } {
  if (method === "cash") {
    return { label: "Cash", cls: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400" };
  }
  if (method === "online") {
    return { label: "Online", cls: "bg-sky-500/20 text-sky-700 dark:text-sky-400" };
  }
  if (method === "pay_order") {
    return { label: "Pay Order", cls: "bg-amber-500/20 text-amber-700 dark:text-amber-400" };
  }
  return { label: "No method", cls: "bg-muted text-muted-foreground" };
}

function paymentStatusBadge(sale: SaleIndexRecord): { label: string; cls: string } {
  if (sale.is_exchanged || sale.void_type === "exchange" || sale.payment_status === "exchanged") {
    return {
      label: "Exchanged",
      cls: "border-transparent bg-violet-600 text-white shadow-md ring-1 ring-black/20",
    };
  }
  if (sale.is_returned || sale.void_type === "return" || sale.payment_status === "returned") {
    return {
      label: "Returned",
      cls: "border-transparent bg-violet-600 text-white shadow-md ring-1 ring-black/20",
    };
  }
  if (sale.is_cancelled || sale.payment_status === "cancelled" || sale.cancelled_at) {
    return {
      label: "Cancelled",
      cls: "border-transparent bg-slate-800 text-white shadow-md ring-1 ring-black/20",
    };
  }

  const paid = Number(sale.paid_total ?? sale.token_amount ?? 0);
  const outstanding = Number(
    sale.outstanding ?? Math.max(0, Number(sale.total_amount ?? 0) - paid),
  );
  const status =
    sale.payment_status ??
    (outstanding <= 0.009 ? "paid" : paid > 0.009 ? "partial" : "unpaid");

  if (status === "paid") {
    return {
      label: "Fully paid",
      cls: "border-transparent bg-emerald-600 text-white shadow-md ring-1 ring-black/20",
    };
  }
  if (status === "partial") {
    return {
      label: "Partially paid",
      cls: "border-transparent bg-amber-500 text-white shadow-md ring-1 ring-black/20",
    };
  }
  return {
    label: "Unpaid",
    cls: "border-transparent bg-rose-600 text-white shadow-md ring-1 ring-black/20",
  };
}

function paymentPlanLabel(sale: SaleIndexRecord): string {
  if (sale.is_installment) {
    const count = Number(sale.installment_count ?? 0);
    return count > 0 ? `${count} installments` : "Installments";
  }
  const total = Number(sale.total_amount ?? 0);
  const token = Number(sale.token_amount ?? 0);
  if (total > 0 && token >= total - 0.009) return "Full payment";
  if (token > 0) return "Token payment";
  return "Balance payment";
}

function saleDisplayDate(s: SaleIndexRecord): Date | null {
  const raw = s.date ?? s.created_at;
  if (!raw) return null;
  const d = parseISO(raw);
  return isValid(d) ? d : null;
}

function invoiceLabel(s: SaleIndexRecord): string {
  return s.invoice_no || String(s.id);
}

const Sales = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canViewAny = can(user, "sale_can_view_any");
  const canViewDetail = can(user, "sale_can_view");
  const canCreate = can(user, "sale_can_create") || can(user, "product_can_view_prices");
  const canUpdate = can(user, "sale_can_update") || can(user, "sale_can_create");
  const [isForbidden, setIsForbidden] = useState(false);
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState<SaleIndexRecord[]>([]);
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filterProduct, setFilterProduct] = useState<SaleProductPick | null>(null);
  const [filterCustomerId, setFilterCustomerId] = useState<string>("__all__");
  const [filterPaymentMethod, setFilterPaymentMethod] = useState<string>("__all__");
  const [filterSaleDate, setFilterSaleDate] = useState<Date | undefined>(undefined);

  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = useState<"amount_desc" | "amount_asc" | "latest" | "oldest" | "invoice_asc" | "customer_asc">("latest");
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");

  const [customerVersion, setCustomerVersion] = useState(0);

  const customers = useMemo(() => listCustomers(), [customerVersion]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setIsForbidden(false);
      try {
        const [rows] = await Promise.all([
          syncSalesFromApi(),
          syncCustomersFromApi().then(() => mounted && setCustomerVersion((v) => v + 1)),
        ]);
        if (mounted) {
          setSales(rows);
          refresh();
        }
      } catch (e) {
        if (mounted && isAxiosError(e) && e.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        if (mounted) toast.error(salesIndexErrorMessage(e));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sales.filter((s) => {
      if (filterProduct && String(s.product_id ?? "") !== filterProduct.id) return false;
      if (filterCustomerId !== "__all__" && String(s.customer_id ?? "") !== filterCustomerId) return false;
      if (filterPaymentMethod !== "__all__" && (s.payment_method ?? "") !== filterPaymentMethod) return false;

      if (filterSaleDate) {
        const want = format(filterSaleDate, "yyyy-MM-dd");
        const parsed = s.date ? parseISO(s.date) : null;
        const saleDate = parsed && isValid(parsed) ? format(parsed, "yyyy-MM-dd") : null;
        if (saleDate !== want) return false;
      }

      if (quickDate !== "all") {
        const when = saleDisplayDate(s);
        if (!when) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today" ? startOfDay(now) : quickDate === "week" ? startOfDay(subDays(now, 6)) : startOfMonth(now);
        if (when < start || when > end) return false;
      }

      if (!q) return true;
      const hay = [
        invoiceLabel(s),
        s.product?.name ?? "",
        s.customer?.name ?? "",
        s.customer?.phone ?? "",
        s.employee?.full_name ?? "",
        paymentMethodLabel(s.payment_method),
        String(s.id),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [sales, query, filterProduct, filterCustomerId, filterPaymentMethod, filterSaleDate, quickDate]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "amount_asc":
          return (a.total_amount ?? 0) - (b.total_amount ?? 0);
        case "amount_desc":
          return (b.total_amount ?? 0) - (a.total_amount ?? 0);
        case "oldest": {
          const da = saleDisplayDate(a)?.getTime() ?? 0;
          const db = saleDisplayDate(b)?.getTime() ?? 0;
          return da - db || a.id - b.id;
        }
        case "invoice_asc":
          return invoiceLabel(a).localeCompare(invoiceLabel(b), undefined, { numeric: true });
        case "customer_asc":
          return (a.customer?.name ?? "").localeCompare(b.customer?.name ?? "");
        case "latest":
        default: {
          const da = saleDisplayDate(a)?.getTime() ?? 0;
          const db = saleDisplayDate(b)?.getTime() ?? 0;
          return db - da || b.id - a.id;
        }
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterProduct ? 1 : 0) +
    (filterCustomerId !== "__all__" ? 1 : 0) +
    (filterPaymentMethod !== "__all__" ? 1 : 0) +
    (filterSaleDate ? 1 : 0);

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterProduct?.id ?? ""}|${filterCustomerId}|${filterPaymentMethod}|${filterSaleDate?.toISOString() ?? ""}|${version}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, { defaultPageSize: 20 });

  if (!canViewAny || isForbidden) {
    return (
      <PermissionDenied
        title="Sales access required"
        message="You don’t have permission to view sales. Ask an administrator to grant sale_can_view_any."
      />
    );
  }

  return (
    <Layout title="Sales">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-display font-bold text-foreground">Sales</h1>
            <p className="text-sm text-muted-foreground mt-1">Record customer purchases and print invoices</p>
          </div>
          {canCreate ? (
            <Link to="/product-sales/new">
              <Button variant="premium" className="gap-2 shrink-0">
                <Plus className="w-4 h-4" />
                <span className="sm:hidden">Add</span>
                <span className="hidden sm:inline">Record sale</span>
              </Button>
            </Link>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by invoice, product, customer, phone, salesperson…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter sales</SheetTitle>
                  <SheetDescription>Refine by product, customer, payment method, or sale date.</SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-5">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Product</Label>
                      <SaleProductPicker
                        value={filterProduct}
                        onChange={setFilterProduct}
                        inventoryStatus="in_stock,sold"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Customer</Label>
                      <Select value={filterCustomerId} onValueChange={setFilterCustomerId}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="All customers" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All customers</SelectItem>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Payment method</Label>
                      <Select value={filterPaymentMethod} onValueChange={setFilterPaymentMethod}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="All methods" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All methods</SelectItem>
                          {PAYMENT_METHODS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Sale date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterSaleDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterSaleDate ? format(filterSaleDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterSaleDate} onSelect={setFilterSaleDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterSaleDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>

                <SheetFooter className="mt-6 gap-2">
                  <Button
                    type="button"
                    variant="glass"
                    className="w-full"
                    onClick={() => {
                      setFilterProduct(null);
                      setFilterCustomerId("__all__");
                      setFilterPaymentMethod("__all__");
                      setFilterSaleDate(undefined);
                    }}
                  >
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect id="sales-page-size" pageSize={pageSize} onPageSizeChange={setPageSize} />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="amount_desc">Sale price: High to Low</SelectItem>
                  <SelectItem value="amount_asc">Sale price: Low to High</SelectItem>
                  <SelectItem value="invoice_asc">Invoice number</SelectItem>
                  <SelectItem value="customer_asc">Customer: A to Z</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect id="sales-page-size-desktop" pageSize={pageSize} onPageSizeChange={setPageSize} />

            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="amount_desc">Sale price: High to Low</SelectItem>
                    <SelectItem value="amount_asc">Sale price: Low to High</SelectItem>
                    <SelectItem value="invoice_asc">Invoice number</SelectItem>
                    <SelectItem value="customer_asc">Customer: A to Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading sales…
          </div>
        ) : null}

        {!loading && filtered.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {sales.length === 0
              ? canCreate
                ? "No sales yet. Click “Record sale” to create one."
                : "No sales yet."
              : "No sales match your search."}
          </div>
        ) : null}

        {!loading && filtered.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {paginated.map((s, i) => {
              const methodBadge = paymentMethodBadge(s.payment_method);
              const statusBadge = paymentStatusBadge(s);
              const when = saleDisplayDate(s);
              const productImageUrl = resolveProductThumbnailUrl(s.product ?? null);
              const paid = Number(s.paid_total ?? s.token_amount ?? 0);
              const outstanding = Number(
                s.outstanding ?? Math.max(0, Number(s.total_amount ?? 0) - paid),
              );
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="h-full"
                >
                  <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-md">
                    <div className="relative h-36 overflow-hidden bg-muted">
                      <ProductImageThumb
                        src={productImageUrl}
                        alt={s.product?.name ?? "Product"}
                        iconClassName="h-10 w-10"
                      />
                      <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-2 bg-gradient-to-b from-black/60 via-black/25 to-transparent p-3">
                        <span className="rounded-md bg-black/55 px-2 py-1 text-xs font-semibold text-white shadow-sm backdrop-blur-[2px]">
                          Invoice {invoiceLabel(s)}
                        </span>
                        <span
                          className={cn(
                            "shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide",
                            statusBadge.cls,
                          )}
                        >
                          {statusBadge.label}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col p-4">
                      <div className="min-w-0">
                        <h3 className="truncate text-base font-semibold text-foreground" title={s.product?.name ?? ""}>
                          {s.product?.name ?? "Unnamed product"}
                        </h3>
                        <div className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                          <User className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                          <span className="truncate">{s.customer?.name ?? "No customer"}</span>
                          {s.customer?.phone ? <span className="shrink-0">· {s.customer.phone}</span> : null}
                        </div>
                      </div>

                      <div className="mt-3 rounded-lg border border-border/40 bg-muted/10 p-3">
                        <div className="flex items-end justify-between gap-3 border-b border-border/30 pb-2">
                          <span className="text-xs text-muted-foreground">Sale price</span>
                          <span className="text-base font-bold tabular-nums text-foreground">
                            {formatSalePrice(s.total_amount)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 pt-2">
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Received</p>
                            <p className="mt-0.5 truncate text-sm font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
                              {formatSalePrice(paid)}
                            </p>
                          </div>
                          <div className="min-w-0 border-l border-border/30 pl-3">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Outstanding</p>
                            <p className={cn(
                              "mt-0.5 truncate text-sm font-semibold tabular-nums",
                              outstanding > 0.009 ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground",
                            )}>
                              {formatSalePrice(outstanding)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 rounded-full border border-border/50 bg-muted/20 px-2 py-1 text-[11px] font-medium text-foreground">
                          <Wallet className="h-3 w-3 text-primary" />
                          {paymentPlanLabel(s)}
                        </span>
                        <span className={cn("rounded-full px-2 py-1 text-[11px] font-medium", methodBadge.cls)}>
                          {methodBadge.label}
                        </span>
                      </div>

                      <div className="mt-3 flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
                        <span>{when ? format(when, "PP") : "No date"}</span>
                        <span className="truncate text-right">{s.employee?.full_name ?? "No salesperson"}</span>
                      </div>

                      <div className="mt-auto flex items-center gap-2 border-t border-border/30 pt-3">
                        {canViewDetail ? (
                          <Button
                            type="button"
                            variant="premium"
                            size="sm"
                            className="h-8 flex-1 gap-1.5 text-xs"
                            onClick={() => navigate(`/product-sales/${s.id}`)}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View sale
                          </Button>
                        ) : null}
                        {canViewDetail &&
                        !(
                          s.is_cancelled ||
                          s.cancelled_at ||
                          s.is_returned ||
                          s.is_exchanged ||
                          s.void_type === "return" ||
                          s.void_type === "exchange" ||
                          s.payment_status === "returned" ||
                          s.payment_status === "exchanged" ||
                          s.payment_status === "cancelled"
                        ) ? (
                          <>
                            <Button
                              type="button"
                              variant="glass"
                              size="sm"
                              className="h-8 flex-1 gap-1.5 text-xs"
                              onClick={() => navigate(`/sale-returns/${s.id}`)}
                            >
                              <Undo2 className="h-3.5 w-3.5" />
                              Sale return
                            </Button>
                            <Button
                              type="button"
                              variant="glass"
                              size="sm"
                              className="h-8 flex-1 gap-1.5 text-xs"
                              onClick={() => navigate(`/car-exchanges/${s.id}`)}
                            >
                              <RefreshCw className="h-3.5 w-3.5" />
                              Car exchange
                            </Button>
                          </>
                        ) : null}
                        <a href={`/sales/${s.id}/pdf`} target="_blank" rel="noreferrer">
                          <Button type="button" variant="glass" size="sm" className="h-8 gap-1.5 px-2.5 text-xs">
                            <Printer className="h-3.5 w-3.5" />
                            PDF
                          </Button>
                        </a>
                      </div>
                    </div>
                  </article>
                </motion.div>
              );
            })}
          </div>
        ) : null}

        {!loading && filtered.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((s, i) => {
              const statusBadge = paymentStatusBadge(s);
              const when = saleDisplayDate(s);
              const productImageUrl = resolveProductThumbnailUrl(s.product ?? null);
              const paid = Number(s.paid_total ?? s.token_amount ?? 0);
              const outstanding = Number(
                s.outstanding ?? Math.max(0, Number(s.total_amount ?? 0) - paid),
              );
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className="glass-card p-3"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="h-16 w-20 shrink-0 overflow-hidden rounded-md border border-border/50 bg-muted">
                      <ProductImageThumb src={productImageUrl} alt={s.product?.name ?? "Product"} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-foreground truncate">{invoiceLabel(s)}</h3>
                        <span className={cn("rounded-full border px-2 py-0.5 text-[11px] font-medium", statusBadge.cls)}>
                          {statusBadge.label}
                        </span>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                          {paymentPlanLabel(s)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {s.product?.name ?? "—"} · {s.customer?.name ?? "—"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        Salesperson: {s.employee?.full_name ?? "—"}
                      </p>
                    </div>
                    <div className="text-right ml-auto">
                      <p className="text-sm font-semibold text-primary">{formatSalePrice(s.total_amount)}</p>
                      <p className="text-[11px] text-emerald-700 dark:text-emerald-400">
                        Paid {formatSalePrice(paid)}
                      </p>
                      <p className="text-[11px] text-amber-700 dark:text-amber-400">
                        Due {formatSalePrice(outstanding)}
                      </p>
                      <p className="text-[11px] text-muted-foreground">{when ? format(when, "PP") : "—"}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-auto flex-wrap justify-end">
                      {canViewDetail ? (
                        <Link to={`/product-sales/${s.id}`}>
                          <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs">
                            <Eye className="w-3 h-3" /> View
                          </Button>
                        </Link>
                      ) : null}
                      {canViewDetail &&
                      !(
                        s.is_cancelled ||
                        s.cancelled_at ||
                        s.is_returned ||
                        s.is_exchanged ||
                        s.void_type === "return" ||
                        s.void_type === "exchange" ||
                        s.payment_status === "returned" ||
                        s.payment_status === "exchanged" ||
                        s.payment_status === "cancelled"
                      ) ? (
                        <>
                          <Link to={`/sale-returns/${s.id}`}>
                            <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs">
                              <Undo2 className="w-3 h-3" /> Sale return
                            </Button>
                          </Link>
                          <Link to={`/car-exchanges/${s.id}`}>
                            <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs">
                              <RefreshCw className="w-3 h-3" /> Car exchange
                            </Button>
                          </Link>
                        </>
                      ) : null}
                      {canUpdate &&
                      !(
                        s.is_cancelled ||
                        s.cancelled_at ||
                        s.payment_status === "cancelled" ||
                        s.is_returned ||
                        s.is_exchanged ||
                        s.void_type === "return" ||
                        s.void_type === "exchange" ||
                        s.payment_status === "returned" ||
                        s.payment_status === "exchanged" ||
                        s.is_installment
                      ) ? (
                        <Link to={`/product-sales/${s.id}/edit`}>
                          <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs">
                            <Edit className="w-3 h-3" /> Edit
                          </Button>
                        </Link>
                      ) : null}
                      <a href={`/sales/${s.id}/pdf`} target="_blank" rel="noreferrer">
                        <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs">
                          <Printer className="w-3 h-3" /> Print
                        </Button>
                      </a>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : null}

        <GridPaginationFooter
          totalCount={totalCount}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </Layout>
  );
};

export default Sales;
